import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import { authCRUD, authActions } from "../../../actions/auth";

const loginSchema = Yup.object().shape({
  username: Yup.string().required("Username is required"),
  password: Yup.string()
    .min(8, "Minimum 8 symbols")
    .max(50, "Maximum 50 symbols")
    .required("Password is required"),
});

const initialValues = {
  username: "",
  password: "",
};

const styles = {
  page: {
    minHeight: "100vh",
    display: "flex",
    background: "#f4f7fb",
    fontFamily: "'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  },
  hero: {
    flex: "1 1 55%",
    position: "relative",
    backgroundImage:
      "linear-gradient(135deg, rgba(12,74,110,0.72) 0%, rgba(8,145,178,0.35) 45%, rgba(15,23,42,0.55) 100%), url('assets/images/zanzibar-community.png')",
    backgroundColor: "#0c4a6e",
    backgroundSize: "cover",
    backgroundPosition: "center",
    color: "#fff",
    padding: "56px 64px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    overflow: "hidden",
  },
  heroPattern: {
    position: "absolute",
    inset: 0,
    background:
      "linear-gradient(180deg, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0) 35%, rgba(0,0,0,0) 60%, rgba(0,0,0,0.55) 100%)",
    pointerEvents: "none",
  },
  brandRow: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    gap: 10,
    position: "relative",
    zIndex: 1,
  },
  brandLogo: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  brandText: { lineHeight: 1.2 },
  brandTitle: { fontSize: 24, fontWeight: 700, letterSpacing: 0.3 },
  brandSubtitle: {
    fontSize: 12,
    opacity: 0.85,
    letterSpacing: 1.2,
    textTransform: "uppercase",
  },
  heroBody: { position: "relative", zIndex: 1, maxWidth: 520 },
  heroHeadline: {
    fontSize: 40,
    fontWeight: 700,
    lineHeight: 1.15,
    marginBottom: 18,
    textShadow: "0 2px 12px rgba(0,0,0,0.25)",
  },
  heroLead: {
    fontSize: 16,
    lineHeight: 1.6,
    opacity: 0.92,
    marginBottom: 28,
  },
  featureList: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 14,
    marginTop: 12,
  },
  featureItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    background: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.18)",
    borderRadius: 12,
    padding: "12px 14px",
    backdropFilter: "blur(6px)",
  },
  featureIcon: {
    width: 34,
    height: 34,
    borderRadius: 8,
    background: "rgba(255,255,255,0.18)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    fontSize: 16,
  },
  featureTitle: { fontSize: 14, fontWeight: 600, marginBottom: 2 },
  featureDesc: { fontSize: 12, opacity: 0.85, lineHeight: 1.4 },
  heroFooter: {
    position: "relative",
    zIndex: 1,
    fontSize: 12,
    opacity: 0.8,
  },

  formPane: {
    flex: "1 1 45%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "48px 32px",
  },
  formCard: {
    width: "100%",
    maxWidth: 440,
  },
  mobileLogo: {
    display: "none",
    justifyContent: "center",
    marginBottom: 24,
  },
  welcomeTitle: {
    fontSize: 28,
    fontWeight: 700,
    color: "#0f172a",
    marginBottom: 8,
  },
  welcomeSub: {
    color: "#64748b",
    fontSize: 14.5,
    marginBottom: 28,
  },
  alert: {
    background: "#fef2f2",
    border: "1px solid #fecaca",
    color: "#b91c1c",
    padding: "10px 14px",
    borderRadius: 10,
    fontSize: 13.5,
    marginBottom: 18,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  fieldGroup: { marginBottom: 18 },
  fieldLabel: {
    display: "block",
    fontSize: 13,
    fontWeight: 600,
    color: "#334155",
    marginBottom: 8,
  },
  inputWrap: { position: "relative" },
  inputIcon: {
    position: "absolute",
    left: 14,
    top: "50%",
    transform: "translateY(-50%)",
    color: "#94a3b8",
    fontSize: 16,
    pointerEvents: "none",
  },
  input: {
    width: "100%",
    height: 48,
    padding: "0 16px 0 42px",
    border: "1px solid #e2e8f0",
    borderRadius: 10,
    fontSize: 14.5,
    color: "#0f172a",
    background: "#fff",
    outline: "none",
    transition: "border-color 0.15s, box-shadow 0.15s",
    boxSizing: "border-box",
  },
  inputError: {
    borderColor: "#ef4444",
    boxShadow: "0 0 0 3px rgba(239,68,68,0.12)",
  },
  passwordToggle: {
    position: "absolute",
    right: 6,
    top: "50%",
    transform: "translateY(-50%)",
    background: "transparent",
    border: "none",
    color: "#64748b",
    padding: "8px 12px",
    cursor: "pointer",
    fontSize: 16,
  },
  errorText: {
    color: "#dc2626",
    fontSize: 12.5,
    marginTop: 6,
    display: "block",
  },
  rowBetween: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 22,
  },
  rememberLabel: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 13.5,
    color: "#475569",
    cursor: "pointer",
  },
  forgot: {
    fontSize: 13.5,
    color: "#0891b2",
    textDecoration: "none",
    fontWeight: 500,
  },
  submit: {
    width: "100%",
    height: 48,
    border: "none",
    borderRadius: 10,
    background:
      "linear-gradient(135deg, #0c4a6e 0%, #0891b2 55%, #059669 100%)",
    color: "#fff",
    fontWeight: 600,
    fontSize: 15,
    letterSpacing: 0.3,
    cursor: "pointer",
    boxShadow: "0 10px 24px rgba(8,145,178,0.28)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    transition: "transform 0.05s, box-shadow 0.15s, opacity 0.15s",
  },
  submitDisabled: {
    opacity: 0.65,
    cursor: "not-allowed",
    boxShadow: "none",
  },
  footerNote: {
    marginTop: 28,
    textAlign: "center",
    fontSize: 12.5,
    color: "#94a3b8",
  },
};

const responsiveCss = `
  @media (max-width: 991px) {
    .auth-hero { display: none !important; }
    .auth-mobile-logo { display: flex !important; }
  }
  .auth-input:focus {
    border-color: #0891b2 !important;
    box-shadow: 0 0 0 3px rgba(8,145,178,0.15) !important;
  }
  .auth-submit:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 14px 28px rgba(8,145,178,0.35);
  }
  .auth-submit:active:not(:disabled) { transform: translateY(0); }
`;

export const SigninContent = () => {
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState(false);
  const dispatch = useDispatch();
  let navigate = useNavigate();

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: (values, { setStatus, setSubmitting }) => {
      setLoading(true);
      setTimeout(async () => {
        const user = { username: values.username, password: values.password };
        authCRUD
          .loadUser(values.username)
          .then((resp) => {
            setLoading(false);
            if (!resp.data.is_active) {
              setStatus("The login detail is incorrect");
            } else {
              dispatch(authActions.getUser(resp.data));
              authCRUD
                .loadToken(user)
                .then((resp) => {
                  dispatch(authActions.setAuth(true));
                  dispatch(authActions.getToken(resp.data));
                  navigate("/dashboard");
                  setLoading(false);
                })
                .catch((error) => {
                  if (error) {
                    setLoading(false);
                    setSubmitting(false);
                    setStatus("The login detail is incorrect");
                  }
                });
            }
          })
          .catch(() => {
            setLoading(false);
            setSubmitting(false);
            setStatus("The login detail is incorrect");
          });
      }, 1000);
    },
  });

  const usernameHasError =
    formik.touched.username && formik.errors.username;
  const passwordHasError =
    formik.touched.password && formik.errors.password;

  return (
    <>
      <style>{responsiveCss}</style>
      <div style={styles.page}>
        {/* Left hero panel */}
        <div className="auth-hero" style={styles.hero}>
          <div style={styles.heroPattern} />


          <div style={styles.brandRow}>
            <div style={styles.brandLogo}>
              <img
                src="assets/images/logo/smz.png"
                alt="SMZ"
                style={{ height: 120, objectFit: "contain" }}
              />
            </div>
            <div style={styles.brandText}>
              <div style={styles.brandTitle}>Shehia Register</div>
              <div style={styles.brandSubtitle}>ZPC · Zanzibar</div>
            </div>
          </div>

          <div style={styles.heroBody}>
            <h1 style={styles.heroHeadline}>
              Digital registry for every Shehia in Zanzibar
            </h1>
            <p style={styles.heroLead}>
              A unified platform for managing buildings, boundaries, and
              community data — empowering planners and local authorities with
              accurate, up-to-date spatial information.
            </p>

            <div style={styles.featureList}>
              <div style={styles.featureItem}>
                <div style={styles.featureIcon}>
                  <i className="bi bi-geo-alt-fill"></i>
                </div>
                <div>
                  <div style={styles.featureTitle}>Spatial Registry</div>
                  <div style={styles.featureDesc}>
                    Buildings & boundaries mapped in real time
                  </div>
                </div>
              </div>
              <div style={styles.featureItem}>
                <div style={styles.featureIcon}>
                  <i className="bi bi-shield-lock-fill"></i>
                </div>
                <div>
                  <div style={styles.featureTitle}>Secure Access</div>
                  <div style={styles.featureDesc}>
                    Role-based authentication
                  </div>
                </div>
              </div>
              <div style={styles.featureItem}>
                <div style={styles.featureIcon}>
                  <i className="bi bi-bar-chart-fill"></i>
                </div>
                <div>
                  <div style={styles.featureTitle}>Insightful Reports</div>
                  <div style={styles.featureDesc}>
                    Planning data at your fingertips
                  </div>
                </div>
              </div>
              <div style={styles.featureItem}>
                <div style={styles.featureIcon}>
                  <i className="bi bi-people-fill"></i>
                </div>
                <div>
                  <div style={styles.featureTitle}>Population Register</div>
                  <div style={styles.featureDesc}>
                    Built for Shehas and local leaders
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div style={styles.heroFooter}>
            &copy; {new Date().getFullYear()} Zanzibar Planning Commission
          </div>
        </div>

        {/* Right form panel */}
        <div style={styles.formPane}>
          <div style={styles.formCard}>
            <div className="auth-mobile-logo" style={styles.mobileLogo}>
              <img
                src="assets/images/logo/smz.png"
                alt="SMZ"
                style={{ height: 64 }}
              />
            </div>

            <h2 style={styles.welcomeTitle}>Welcome back</h2>
            <p style={styles.welcomeSub}>
              Sign in to continue to the Shehia Register dashboard.
            </p>

            {formik.status && (
              <div style={styles.alert} role="alert">
                <i className="bi bi-exclamation-triangle-fill"></i>
                <span>{formik.status}</span>
              </div>
            )}

            <form onSubmit={formik.handleSubmit} noValidate>
              <div style={styles.fieldGroup}>
                <label style={styles.fieldLabel} htmlFor="username">
                  Username
                </label>
                <div style={styles.inputWrap}>
                  <i
                    className="bi bi-person-fill"
                    style={styles.inputIcon}
                  ></i>
                  <input
                    id="username"
                    type="text"
                    className="auth-input"
                    style={{
                      ...styles.input,
                      ...(usernameHasError ? styles.inputError : {}),
                    }}
                    placeholder="Enter your username"
                    autoComplete="username"
                    {...formik.getFieldProps("username")}
                  />
                </div>
                {usernameHasError && (
                  <span role="alert" style={styles.errorText}>
                    {formik.errors.username}
                  </span>
                )}
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.fieldLabel} htmlFor="password-input">
                  Password
                </label>
                <div style={styles.inputWrap}>
                  <i
                    className="bi bi-lock-fill"
                    style={styles.inputIcon}
                  ></i>
                  <input
                    id="password-input"
                    type={checked ? "text" : "password"}
                    className="auth-input"
                    style={{
                      ...styles.input,
                      paddingRight: 48,
                      ...(passwordHasError ? styles.inputError : {}),
                    }}
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    {...formik.getFieldProps("password")}
                  />
                  <button
                    type="button"
                    onClick={() => setChecked(!checked)}
                    style={styles.passwordToggle}
                    aria-label={checked ? "Hide password" : "Show password"}
                  >
                    <i
                      className={
                        checked ? "bi bi-eye-slash-fill" : "bi bi-eye-fill"
                      }
                    ></i>
                  </button>
                </div>
                {passwordHasError && (
                  <span role="alert" style={styles.errorText}>
                    {formik.errors.password}
                  </span>
                )}
              </div>

              <div style={styles.rowBetween}>
                <label style={styles.rememberLabel}>
                  <input
                    type="checkbox"
                    id="auth-remember-check"
                    style={{ accentColor: "#0891b2" }}
                  />
                  Remember me
                </label>
                <a href="#" style={styles.forgot}>
                  Forgot password?
                </a>
              </div>

              <button
                type="submit"
                className="auth-submit"
                style={{
                  ...styles.submit,
                  ...(formik.isSubmitting || !formik.isValid || loading
                    ? styles.submitDisabled
                    : {}),
                }}
                disabled={formik.isSubmitting || !formik.isValid || loading}
              >
                {!loading && (
                  <>
                    <i className="bi bi-box-arrow-in-right"></i>
                    <span>Sign in</span>
                  </>
                )}
                {loading && (
                  <>
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    <span>Signing you in…</span>
                  </>
                )}
              </button>
            </form>

            <div style={styles.footerNote}>
              Crafted with <i className="mdi mdi-heart text-danger"></i> by{" "}
              <a
                target="_blank"
                rel="noreferrer"
                href="https://suza.ac.tz"
                style={{ color: "#0891b2", textDecoration: "none" }}
              >
                SUZA
              </a>{" "}
              for ZPC
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
